import type { Asset, Entry } from 'contentful'
import type { ImageSizes } from '@components/contentful-image'
import { DEVICE_SIZES } from '@lib/constants-node.cjs'
import type { TypeCaptionedMediaFields, TypeContentBlockFields } from '@lib/generated-types'
import { campaignHeroDimensionMap } from '@lib/token-maps'
import { type ContentfulImagesAPI } from '@lib/types'
import { ensureProtocol } from '@lib/utilities/page-utilities'

export const CONTENTFUL_IMAGE_DEFAULT_QUALITY = '85'

export type WidthKeys = TypeContentBlockFields['width']

/**
 * WidthsAtBreakpoint defines the pixel width for each responsive breakpoint used in image generation.
 *
 * - xs: 0–639px — Extra small screens (e.g., most phones)
 * - sm: 640–767px — Small screens (e.g., large phones, small tablets)
 * - md: 768–1023px — Medium screens (e.g., tablets, small laptops)
 * - lg: 1024–1279px — Large screens (e.g., laptops, desktops)
 * - xl: 1280–1535px — Extra large screens (e.g., large desktops)
 * - max: 1536px and up — 2xl and above (required, used as fallback for largest size)
 *
 * All properties are optional except `max`, which is required.
 */
export type WidthsAtBreakpoint = {
  /** 0–639px — corresponds to xs breakpoint */
  xs?: number
  /** 640–767px — corresponds to sm breakpoint */
  sm?: number
  /** 768–1023px — corresponds to md breakpoint */
  md?: number
  /** 1024–1279px — corresponds to lg breakpoint */
  lg?: number
  /** 1280–1535px — corresponds to xl breakpoint */
  xl?: number
  /** 1536px and up — corresponds to max/2xl breakpoint */
  max: number
}

// Main Exports (public API)

/**
 * Builds Contentful image API params, including focal point if provided.
 */
export const buildContentfulImageParams = (
  focalPoint?: ContentfulImagesAPI['f']
): ContentfulImagesAPI => {
  if (focalPoint) {
    return {
      f: focalPoint,
      fit: 'fill',
      fm: 'webp',
      q: CONTENTFUL_IMAGE_DEFAULT_QUALITY,
    }
  }
  return {
    q: CONTENTFUL_IMAGE_DEFAULT_QUALITY,
  }
}

/**
 * Wrapper for buildContentfulImageParams. Builds a Contentful image URL with merged params (new values overwrite old ones).
 * If the URL already has Contentful image params, the new values from buildContentfulImageParams will overwrite them.
 */
export const buildContentfulImageUrl = (
  url: string,
  focalPoint?: ContentfulImagesAPI['f']
): string => {
  if (!url) return ''
  const safeUrl = ensureProtocol(url)
  const urlObj = new URL(safeUrl)
  // Get existing params
  const baseParams = Object.fromEntries(urlObj.searchParams.entries())
  // Get new params
  const newParams = buildContentfulImageParams(focalPoint)
  // Merge params as plain objects to avoid issues with URLSearchParams instances.
  // Both baseParams and newParams are plain objects here.
  const mergedParamsObj = { ...baseParams, ...newParams }
  const mergedParams = new URLSearchParams(mergedParamsObj)
  mergedParams.delete('v')
  return urlObj.origin + urlObj.pathname + '?' + mergedParams.toString()
}

/**
 * Converts an aspect ratio string (e.g., "16:9") to a number (e.g., 16/9).
 * @param ar - The aspect ratio string in the format "width:height" (e.g., "16:9").
 * @returns The aspect ratio as a number (width divided by height), or undefined if the input is invalid.
 */
export const aspectRatioStringToNumber = (ar: string | undefined) => {
  if (!ar || !ar.includes(':')) return undefined
  const [w, h] = ar.split(':')
  return Number(w) / Number(h)
}

/**
 * Helper to calculate the intrinsic (native) aspect ratio of a Contentful image asset.
 * @param image - The Contentful image Asset object.
 * @returns The aspect ratio as width / height (e.g., 1.777 for 16:9).
 */
export const getNativeAspectRatio = (image: Asset): number => {
  const imgWidth = image.fields.file?.details?.image?.width ?? 1
  const imgHeight = image.fields.file?.details?.image?.height ?? 1
  return imgWidth / imgHeight
}

export type GenerateSrcSetAndSizesParams = {
  /** The image URL to use for generating srcSet and sizes. Include Contentful image API params (e.g., q, fm, fit, f, etc.) as query parameters. */
  src: string
  /** The widths to use at each responsive breakpoint. 0 will skip the breakpoint. */
  breakpointWidths: WidthsAtBreakpoint
  /** The aspect ratio (width / height) to use for the image. */
  aspectRatio: number
}

/**
 * Generates a responsive srcSet and sizes string for an image URL, based on provided breakpoints and aspect ratio.
 * This is used to create optimized <img> tags with exacting sizes and sourcesets for different device widths and pixel densities.
 */
export function generateSrcSetAndSizes({
  src,
  breakpointWidths = {
    xs: 640,
    sm: 768,
    md: 1024,
    lg: 1280,
    xl: 1540,
    max: 1920,
  },
  aspectRatio,
}: GenerateSrcSetAndSizesParams): { srcSet: string[]; sizes: string } {
  if (!src) return { srcSet: [], sizes: '' }

  // Parse the base URL and query params from src
  const safeSrc = ensureProtocol(src)
  const urlObj = new URL(safeSrc)
  const baseUrl = urlObj.origin + urlObj.pathname
  const baseParams = new URLSearchParams(urlObj.search)

  // Normalizes the widths input and returns an array of widths for each breakpoint.
  const widthsArray = normalizeWidths(breakpointWidths)

  // Get an array of valid breakpoints (with widths > 0) for which images should be generated.
  const validBreakpoints = getValidBreakpoints(widthsArray)

  // Build the `sizes` (media query) attribute string for the <img> tag.
  const sizes = buildSizesString(validBreakpoints)

  // Generate the srcSet array for the <img> tag.
  const srcSet = buildSrcSetWithBaseParams(baseUrl, baseParams, validBreakpoints, aspectRatio)
  return { srcSet, sizes }
}

/**
 * Internal/Helper Functions
 */

/**
 * Internal function. Returns an array of valid breakpoints with their corresponding widths for responsive images.
 * Each breakpoint is represented as an object with:
 *   - bp: the device breakpoint value (from DEVICE_SIZES)
 *   - width: the pixel width for that breakpoint (0 if not set)
 *   - i: the index in the widths array
 * Only breakpoints with width > 0 are included.
 * Additionally, the last width (if > 0) is added as a breakpoint for screens above the last defined breakpoint (bp: Infinity).
 *
 * @param widths - An array of pixel widths for each breakpoint, in DEVICE_SIZES order.
 * @returns An array of objects with { bp, width, i } for each valid breakpoint.
 * @internal
 */
const getValidBreakpoints = (widths: number[]) => {
  const breakpoints = DEVICE_SIZES.map((bp: number, i: number) => ({
    bp,
    width: widths[i] ?? 0,
    i,
  })).filter((item: { bp: number; width: number; i: number }) => item.width > 0)

  // Add the last width (for screens above the last breakpoint)
  const lastWidth = widths[widths.length - 1]
  if (lastWidth > 0) {
    breakpoints.push({
      bp: Infinity, // or null, just to mark it's the max
      width: lastWidth,
      i: widths.length - 1,
    })
  }

  return breakpoints
}

/**
 * Internal function. Builds a sizes attribute string for a responsive <img> tag based on valid breakpoints.
 * For each breakpoint except the last, generates a media query in the form "(max-width: [bp - 1]px) [width]px".
 * The last breakpoint's width is used as the default size.
 *
 * Example output: "(max-width: 639px) 320px, (max-width: 1023px) 640px, 1280px"
 *
 * @param validBreakpoints - An array of objects with `bp` (breakpoint) and `width` properties.
 * @returns A string suitable for the `sizes` attribute of an <img> tag.
 * @internal
 */
const buildSizesString = (validBreakpoints: { bp: number; width: number }[]): string => {
  const sizesParts: string[] = []
  validBreakpoints.forEach(({ bp, width }, i) => {
    if (i < validBreakpoints.length - 1) {
      sizesParts.push(`(max-width: ${bp - 1}px) ${width}px`)
    }
  })
  if (validBreakpoints.length > 0) {
    sizesParts.push(`${validBreakpoints[validBreakpoints.length - 1].width}px`)
  }
  return sizesParts.join(', ')
}

/**
 * Internal function. Generates a responsive srcSet array for an image URL, based on provided breakpoints, aspect ratio, and base query params.
 * For each valid breakpoint and for each pixel density (1x, 2x, 3x), this function generates a URL with the appropriate width (`w`) and height (`h`)
 * query parameters, and appends the width descriptor (e.g., "800w") for use in the srcSet attribute of an <img> tag.
 *
 * @param baseUrl - The base image URL (without query parameters).
 * @param baseParams - The base query parameters to include in every image URL (e.g., quality, format, fit, etc.).
 * @param validBreakpoints - An array of objects with a `width` property, representing the widths to generate images for.
 * @param aspectRatio - The aspect ratio (width / height) to use for each generated image.
 * @returns An array of srcSet entries, each in the format: "<url> <width>w"
 * @internal
 */
const buildSrcSetWithBaseParams = (
  baseUrl: string,
  baseParams: URLSearchParams,
  validBreakpoints: { width: number }[],
  aspectRatio: number
): string[] => {
  const pixelDensities = [1, 2, 3]
  const srcSet: { width: number; src: string }[] = []

  for (const { width } of validBreakpoints) {
    for (const pixelDensity of pixelDensities) {
      const itemWidth = Math.ceil(width * pixelDensity)
      const itemHeight = Math.ceil(itemWidth / aspectRatio)
      // Merge/override w/h for each srcset entry
      const params = new URLSearchParams(baseParams)
      params.set('w', String(itemWidth))
      params.set('h', String(itemHeight))
      params.delete('v')
      const src = ensureProtocol(`${baseUrl}?${params.toString()} ${itemWidth}w`)
      srcSet.push({ width: itemWidth, src })
    }
  }
  srcSet.sort((a, b) => a.width - b.width)
  return srcSet.map((item) => item.src)
}

/**
 * Internal function. Normalizes a WidthsAtBreakpoint object or a single number into an array of widths for each breakpoint.
 * If a number is provided, it is used as the `max` breakpoint and all others are set to 0.
 * @internal
 */
const normalizeWidths = (widths: WidthsAtBreakpoint | number): number[] => {
  const normalized: WidthsAtBreakpoint = typeof widths === 'number' ? { max: widths } : widths
  return [
    normalized.xs ?? 0, // xs: up to 639px
    normalized.sm ?? 0, // sm: 640px–767px
    normalized.md ?? 0, // md: 768px–1023px
    normalized.lg ?? 0, // lg: 1024px–1279px
    normalized.xl ?? 0, // xl: 1280px–1535px
    normalized.max, // max: 1536px and up (required)
  ]
}

/**
 * Deprecated Functionality
 */

/**
 * @deprecated Use WidthsAtBreakpoint or responsive image utilities instead.
 */
export interface MediaQueryPercentageWidth {
  mediaQuery: string
  w: number
}

/**
 * @deprecated Use generateSrcSetAndSizes() instead.
 */
export const generateBaseMediaQWidths = (
  widthInColumns?: WidthKeys | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
): MediaQueryPercentageWidth[] | undefined => {
  let maxColumns = 12
  if (typeof widthInColumns === 'string') {
    maxColumns = parseInt(widthInColumns.replace(/\W/g, ''), 10)
  }
  const percentageViewport = maxColumns / 12

  // Using Sharp's standard breakpoints defined in the DSM: https://sharphealthcare.invisionapp.com/dsm/sharp-health-care/dxp-redesign/nav/5fa7cb698c0120001835cd74/folder/60e8b857b69cf4312a5c45a8?mode=preview
  return [
    { mediaQuery: 'default', w: 400 },
    { mediaQuery: 'xs', w: 600 }, // 640px  - (20px * 2) = 600px
    { mediaQuery: 'sm', w: 720 }, // 768px  - (24px * 2) = 720px
    { mediaQuery: 'md', w: Math.ceil(percentageViewport * 960) }, // 1024px - (32px * 2) = 960px
    { mediaQuery: 'lg', w: Math.ceil(percentageViewport * 1140) }, // 1280px - (70px * 2) = 1140px
    { mediaQuery: 'xl', w: Math.ceil(percentageViewport * 1396) }, // 1536px - (70px * 2) = 1396px
  ]
}

/**
 * @deprecated - This is currently only used in campaign-hero-block. Update campaign-hero-block to match hero-block
 */
export const generateCaptionedImageSizes = (
  heroHeight: string,
  image: Entry<TypeCaptionedMediaFields>
): ImageSizes[] => {
  return campaignHeroDimensionMap[heroHeight].map((value) => {
    return {
      mediaQuery: value.mediaQuery,
      params: {
        w: value.params.w !== undefined ? String(value.params.w) : undefined,
        fit: 'fill',
        f: image?.fields.focalPoint?.toLowerCase() as Lowercase<
          NonNullable<ContentfulImagesAPI['f']>
        >,
      },
    }
  })
}
